import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, MessageCircleQuestion, Play, Send, Sparkles, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API, api, getStudentId } from "@/lib/api";
import { setAudioPlaying } from "@/lib/audioLock";
import Navbar from "@/components/Navbar";
import VisualWidget from "@/components/VisualWidget";
import FractionText from "@/components/FractionText";
import VoiceRecorder from "@/components/VoiceRecorder";
import DiagnosticQuiz from "@/components/DiagnosticQuiz";

const MODE_TITLES = {
  learn: "Learning Session", homework: "Homework Help", revise: "Revision Session", explore: "Explore",
};

const HELPER_CHIPS = [
  { label: "Give me a hint", icon: "💡", testId: "chip-hint", message: "Can you give me a hint?" },
  { label: "Show an example", icon: "📐", testId: "chip-example", message: "Can you show me an example?" },
  { label: "Explain like I'm 5", icon: "🎈", testId: "chip-eli5", message: "Can you explain it like I'm 5 years old?" },
  { label: "Try another problem", icon: "🔄", testId: "chip-next", message: "Can we try another problem?" },
];

function sanitizeLiveText(raw) {
  const idx = raw.indexOf("```");
  let t = idx >= 0 ? raw.slice(0, idx) : raw;
  t = t.replace(/\*\*(.*?)\*\*/g, "$1").replace(/__(.*?)__/g, "$1").replace(/`([^`]*)`/g, "$1");
  t = t.replace(/<\/?(break|emphasis|prosody|speak|phoneme|say-as)[^>]*>/gi, "");
  return t.trim();
}

export default function TutorSession() {
  const { mode, conceptId } = useParams();
  const navigate = useNavigate();
  const studentId = getStudentId();

  const [student, setStudent] = useState(null);
  const [conceptName, setConceptName] = useState("");
  const [needsDiagnostic, setNeedsDiagnostic] = useState(false);
  const [checkingReadiness, setCheckingReadiness] = useState(mode === "learn" && !!conceptId);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [conceptOps, setConceptOps] = useState([]);
  const [teachBackOpen, setTeachBackOpen] = useState(false);
  const [teachBackText, setTeachBackText] = useState("");
  const [teachBackResult, setTeachBackResult] = useState(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [voicePreview, setVoicePreview] = useState(null);
  const [dismissedQuickReplyIdx, setDismissedQuickReplyIdx] = useState(null);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);
  const photoInputRef = useRef(null);

  const stopAllAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setAudioPlaying(false);
  };

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      stopAllAudio();
    };
  }, []);

  useEffect(() => {
    api.get(`/students/${studentId}`).then(({ data }) => setStudent(data));
    if (conceptId) {
      api.get(`/curriculum/concepts/${conceptId}`).then(({ data }) => {
        setConceptName(data.name);
        const ops = [];
        if (data.visual) ops.push({ type: "visual", widget: data.visual.widget, params: data.visual.params });
        if (data.context_image) ops.push({ type: "image", tag: data.context_image });
        if (ops.length) setConceptOps(ops);
      });
    }
  }, [studentId, conceptId]);

  useEffect(() => {
    const decide = async () => {
      if (mode === "learn" && conceptId) {
        const { data: masteryList } = await api.get(`/mastery/${studentId}`);
        const exists = masteryList.some((m) => m.concept_id === conceptId);
        if (!exists) {
          setNeedsDiagnostic(true);
          setCheckingReadiness(false);
          return;
        }
      }
      setCheckingReadiness(false);
      startSession();
    };
    decide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const startSession = async () => {
    stopAllAudio();
    const { data } = await api.post("/tutor/sessions", { student_id: studentId, concept_id: conceptId || null, mode });
    setSessionId(data.session_id);
    const greetKey = `smartguru_greeted_${studentId}`;
    const alreadyGreeted = sessionStorage.getItem(greetKey) === "1";
    await sendMessage(alreadyGreeted ? "__switch__" : "__start__", data.session_id);
    sessionStorage.setItem(greetKey, "1");
  };

  const handleDiagnosticComplete = () => {
    setNeedsDiagnostic(false);
    startSession();
  };

  const playNextInQueue = () => {
    const next = audioQueueRef.current.shift();
    if (!next) {
      isPlayingRef.current = false;
      setAudioPlaying(false);
      return;
    }
    isPlayingRef.current = true;
    setAudioPlaying(true);
    try {
      const audio = new Audio(`data:audio/mp3;base64,${next.audio_base64}`);
      audioRef.current = audio;
      audio.onended = playNextInQueue;
      audio.onerror = playNextInQueue;
      audio.play().catch(() => {
        isPlayingRef.current = false;
        setAudioPlaying(false);
        audioQueueRef.current.unshift(next);
        setAudioBlocked(true);
      });
    } catch {
      playNextInQueue();
    }
  };

  const enqueueAudio = (chunk) => {
    audioQueueRef.current.push(chunk);
    if (!isPlayingRef.current) playNextInQueue();
  };

  const unlockAudio = () => {
    setAudioBlocked(false);
    if (!isPlayingRef.current) playNextInQueue();
  };

  const replayMessage = (chunks) => {
    if (!chunks?.length) return;
    if (audioRef.current) audioRef.current.pause();
    audioQueueRef.current = [...chunks];
    isPlayingRef.current = false;
    playNextInQueue();
  };

  const sendMessage = async (text, overrideSessionId, imageFile) => {
    const sid = overrideSessionId || sessionId;
    if (!sid || streaming) return;
    const isStart = text === "__start__" || text === "__switch__";
    let imageBase64 = null;
    let imageContentType = null;
    let imagePreviewUrl = null;
    if (imageFile) {
      imageContentType = imageFile.type || "image/jpeg";
      imagePreviewUrl = URL.createObjectURL(imageFile);
      imageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(imageFile);
      });
    }
    if (!isStart) {
      setMessages((prev) => [...prev, { role: "student", content: text, imagePreviewUrl }]);
    }
    setInput("");
    setStreaming(true);
    setVoicePreview(null);
    const initialOps = messages.length === 0 ? conceptOps : [];
    setMessages((prev) => [...prev, { role: "tutor", content: "", rawContent: "", complete: false, showTranscript: false, audioChunks: [], quickReplies: [], ops: initialOps }]);

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const res = await fetch(`${API}/tutor/sessions/${sid}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, image_base64: imageBase64, image_content_type: imageContentType }),
        signal: controller.signal,
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const evt = JSON.parse(part.slice(6));
          if (evt.type === "delta") {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              const rawContent = (last.rawContent || "") + evt.content;
              next[next.length - 1] = { ...last, rawContent, content: sanitizeLiveText(rawContent) };
              return next;
            });
          } else if (evt.type === "final_text") {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { ...next[next.length - 1], content: evt.content, complete: true, quickReplies: evt.quick_replies || [] };
              return next;
            });
          } else if (evt.type === "whiteboard") {
            if (evt.ops?.length) {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                next[next.length - 1] = { ...last, ops: [...(last.ops || []), ...evt.ops] };
                return next;
              });
            }
          } else if (evt.type === "audio_chunk") {
            const chunk = { audio_base64: evt.audio_base64, text: evt.text, is_question: evt.is_question };
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              next[next.length - 1] = {
                ...last,
                audioChunks: [...(last.audioChunks || []), chunk],
                question: evt.is_question ? evt.text : last.question,
              };
              return next;
            });
            enqueueAudio(chunk);
          }
        }
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Tutor connection interrupted.");
    } finally {
      setStreaming(false);
    }
  };

  const toggleTranscript = (index) => {
    setMessages((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], showTranscript: !next[index].showTranscript };
      return next;
    });
  };

  const submitTeachBack = async () => {
    if (!teachBackText.trim()) return;
    const { data } = await api.post("/tutor/teach-back", { student_id: studentId, concept_id: conceptId, explanation_text: teachBackText });
    setTeachBackResult(data);
  };

  if (checkingReadiness) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Preparing your lesson...</div>;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar student={student} />
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 flex-1 w-full min-w-0">
        <div className="flex items-center justify-between mb-4 gap-2">
          <button onClick={() => navigate("/")} data-testid="tutor-back-button" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground shrink-0">
            <ArrowLeft size={15} /> <span className="hidden sm:inline">Back to Home</span>
          </button>
        </div>
        <h1 className="text-lg sm:text-xl font-heading font-bold mb-1 truncate">{MODE_TITLES[mode] || "Tutor"}{conceptName ? `: ${conceptName}` : ""}</h1>

        {audioBlocked && (
          <button
            onClick={unlockAudio}
            data-testid="audio-unlock-banner"
            className="w-full mb-3 flex items-center justify-center gap-2 text-sm font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/30 rounded-xl py-2.5 animate-pulse"
          >
            <Volume2 size={15} /> Tap to hear SmartGuru's voice
          </button>
        )}

        {needsDiagnostic ? (
          <div className="bg-card border border-border rounded-2xl p-6 mt-4 max-w-lg mx-auto">
            <DiagnosticQuiz conceptId={conceptId} onComplete={handleDiagnosticComplete} />
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl flex flex-col h-[calc(100vh-170px)] min-h-[520px] mt-4" data-testid="tutor-chat-panel">
            <div ref={scrollRef} className="flex-1 min-h-[60%] overflow-y-auto scrollbar-thin p-4 flex flex-col justify-end gap-4" data-testid="tutor-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.role}-${i}`}>
                  {m.role === "student" ? (
                    <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-secondary text-secondary-foreground">
                      {m.imagePreviewUrl && (
                        <img src={m.imagePreviewUrl} alt="Uploaded homework" data-testid={`chat-homework-photo-${i}`} className="rounded-xl max-h-52 w-auto object-contain mb-2 border border-secondary-foreground/20" />
                      )}
                      {m.content}
                    </div>
                  ) : (
                    <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-muted text-foreground">
                      {!m.complete ? (
                        <div className="flex items-center gap-2" data-testid={`chat-message-speaking-${i}`}>
                          <span className="flex gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                          </span>
                          <span className="text-xs text-muted-foreground italic">SmartGuru is speaking...</span>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => replayMessage(m.audioChunks)}
                              data-testid={`tts-play-button-${i}`}
                              disabled={!m.audioChunks?.length}
                              className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 shrink-0 disabled:opacity-40"
                              title="Replay"
                            >
                              <Play size={13} />
                            </button>
                            <span className="text-xs text-muted-foreground italic">Tap to hear this again</span>
                          </div>
                          {m.question && (
                            <p className="mt-2.5 font-bold" data-testid={`chat-question-text-${i}`}>{m.question}</p>
                          )}
                          {!!m.ops?.length && (
                            <div className="mt-3 flex flex-wrap gap-2" data-testid={`chat-ops-${i}`}>
                              {m.ops.map((op, oi) => (
                                op.type === "visual" || op.type === "image" ? (
                                  <div key={oi} className="w-full bg-card border border-border rounded-xl p-2 animate-pop-in">
                                    <VisualWidget op={op} />
                                  </div>
                                ) : (
                                  <div key={oi} className="font-notebook text-lg bg-card border border-border rounded-lg px-3 py-1.5 animate-pop-in" data-testid={`chat-op-text-${i}-${oi}`}>
                                    <FractionText text={op.text} />
                                  </div>
                                )
                              ))}
                            </div>
                          )}
                          <button onClick={() => toggleTranscript(i)} data-testid={`transcript-toggle-button-${i}`} className="text-xs font-semibold text-primary mt-2 underline">
                            {m.showTranscript ? "Hide transcript" : "Show transcript"}
                          </button>
                          {m.showTranscript && (
                            <p className="whitespace-pre-wrap mt-2 pt-2 border-t border-border" data-testid={`transcript-text-${i}`}>{m.content}</p>
                          )}
                          {i === messages.length - 1 && !!m.quickReplies?.length && dismissedQuickReplyIdx !== i && (
                            <div className="flex flex-wrap gap-2 mt-3" data-testid={`quick-reply-row-${i}`}>
                              {m.quickReplies.map((opt) => (
                                <button
                                  key={opt}
                                  data-testid={`quick-reply-${opt.replace(/\s+/g, "-")}`}
                                  disabled={streaming}
                                  onClick={() => sendMessage(opt)}
                                  className="text-sm font-bold bg-primary text-primary-foreground rounded-full px-4 py-2 hover:opacity-90 active:scale-95 transition-all disabled:opacity-40"
                                >
                                  {opt}
                                </button>
                              ))}
                              <button
                                data-testid="quick-reply-type-or-speak"
                                disabled={streaming}
                                onClick={() => {
                                  setDismissedQuickReplyIdx(i);
                                  inputRef.current?.focus();
                                }}
                                className="text-sm font-semibold bg-card border-2 border-dashed border-primary/40 text-primary rounded-full px-4 py-2 hover:bg-primary/5 active:scale-95 transition-all disabled:opacity-40"
                              >
                                ✏️ I want to type or speak
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="shrink-0 max-h-[40%] flex flex-col" data-testid="tutor-input-footer">
              <div className="overflow-y-auto scrollbar-thin">
                {conceptId && (
                  <div className="px-4 pb-1 pt-2">
                    <button onClick={() => setTeachBackOpen((v) => !v)} data-testid="teach-back-toggle-button" className="text-xs font-medium text-primary flex items-center gap-1 mb-2">
                      <MessageCircleQuestion size={13} /> Explain this to me in your own words
                    </button>
                    {teachBackOpen && (
                      <div className="mb-3 bg-primary/5 border border-primary/20 rounded-xl p-3" data-testid="teach-back-panel">
                        {!teachBackResult ? (
                          <>
                            <Textarea data-testid="teach-back-textarea" value={teachBackText} onChange={(e) => setTeachBackText(e.target.value)}
                              placeholder="Explain the concept in your own words..." className="text-sm mb-2 bg-card" />
                            <Button size="sm" data-testid="teach-back-submit-button" onClick={submitTeachBack}>Submit</Button>
                          </>
                        ) : (
                          <div className="text-sm" data-testid="teach-back-result">
                            <p className="mb-1">{teachBackResult.feedback}</p>
                            <p className="font-semibold text-primary">{teachBackResult.follow_up_question}</p>
                            <Button size="sm" variant="ghost" className="mt-2" onClick={() => { setTeachBackResult(null); setTeachBackText(""); setTeachBackOpen(false); }}>Close</Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="px-3 sm:px-4 pt-2 flex gap-2 overflow-x-auto scrollbar-thin whitespace-nowrap" data-testid="helper-chips-row">
                  {HELPER_CHIPS.map((chip) => (
                    <button
                      key={chip.testId}
                      data-testid={chip.testId}
                      disabled={streaming || needsDiagnostic}
                      onClick={() => sendMessage(chip.message)}
                      className="text-xs sm:text-sm font-semibold bg-card border border-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                    >
                      {chip.icon} {chip.label}
                    </button>
                  ))}
                </div>

                {voicePreview && (
                  <div className="px-3 sm:px-4 pt-2" data-testid="voice-review-bar">
                    <div className="bg-accent/10 border-2 border-accent/30 rounded-2xl p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-muted-foreground mb-0.5">🎙️ I heard:</p>
                        <p className="text-sm font-semibold truncate">"{voicePreview}"</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          data-testid="voice-review-edit-button"
                          onClick={() => { setInput(voicePreview); setVoicePreview(null); inputRef.current?.focus(); }}
                          className="text-xs font-bold bg-card border border-border rounded-full px-3 py-2 hover:border-primary"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          data-testid="voice-review-send-button"
                          onClick={() => { const t = voicePreview; setVoicePreview(null); sendMessage(t); }}
                          className="text-xs font-bold bg-primary text-primary-foreground rounded-full px-3 py-2 hover:opacity-90"
                        >
                          🚀 Send
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 sm:p-4 shrink-0">
                <div className="flex items-center gap-2 bg-background border-2 border-primary/20 focus-within:border-primary rounded-2xl p-1.5 sm:p-2 transition-colors">
                  {mode === "homework" && (
                    <>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        data-testid="homework-photo-input"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) sendMessage(input.trim(), null, file);
                          e.target.value = "";
                        }}
                      />
                      <button
                        type="button"
                        data-testid="homework-photo-upload-button"
                        disabled={streaming}
                        onClick={() => photoInputRef.current?.click()}
                        className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center hover:bg-accent/20 active:scale-95 transition-all disabled:opacity-40"
                        title="Upload a photo of your work"
                      >
                        <Camera size={16} />
                      </button>
                    </>
                  )}
                  <VoiceRecorder onTranscript={(text) => setVoicePreview(text)} />
                  <input
                    ref={inputRef}
                    data-testid="tutor-chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && input.trim()) sendMessage(input.trim()); }}
                    placeholder="Type here... like 'How do I solve this?'"
                    className="flex-1 bg-transparent px-2 py-2 text-sm sm:text-base outline-none min-w-0"
                  />
                  <button data-testid="tutor-chat-send-button" disabled={!input.trim() || streaming} onClick={() => sendMessage(input.trim())}
                    className="bg-success hover:opacity-90 text-white font-bold rounded-xl px-3 sm:px-4 py-2.5 flex items-center gap-1.5 shrink-0 disabled:opacity-40 active:scale-95 transition-all">
                    <Send size={15} /> <span className="hidden sm:inline">Send</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
